import { Chat } from "@ai-sdk/vue";
import {
  DefaultChatTransport,
  getToolOrDynamicToolName,
  isToolOrDynamicToolUIPart,
  lastAssistantMessageIsCompleteWithApprovalResponses,
  type UIMessage,
} from "ai";
import { ref, computed, watch } from "vue";
import { storeToRefs } from "pinia";
import { useRoute } from "vue-router";
import { useProject } from "@stores/project/project.store";
import { useDashboard } from "@stores/dashboard/dashboard.store";
import { useAuthStateStore } from "@/stores/Utils/auth/AuthState";
import { getMessageText, isUiRenderToolName } from "../utils/assistantRenderParts";
import { useResponseRating } from "./useResponseRating";

const STUCK_TIMEOUT_MS = 30_000;

type OptimisticMessage = UIMessage & { role: "user" };

export function useAgentChat() {
  const agentServiceUrl = (import.meta.env.VITE_AGENT_SERVICE_URL ?? "").replace(/\/$/, "");
  const agentApiUrl = import.meta.env.DEV || !agentServiceUrl ? "/api/orchestrate" : `${agentServiceUrl}/api/orchestrate`;

  const projectStore = useProject();
  const { project } = storeToRefs(projectStore);

  const authStore = useAuthStateStore();
  const { user } = storeToRefs(authStore);

  const dashboard = useDashboard();
  // Can't know the agent's write-tool names, so every non-render_* tool is treated as potentially
  // data-changing. Dedupe by toolCallId; the actual refetch is deferred to the turn-settled watcher.
  const settledDataToolCallIds = new Set<string>();
  let pendingModelRefresh = false;

  // "How was this answer?" — the 1st, 5th, 10th, ... completed response asks for a 1-5 rating. The
  // composable owns the id bookkeeping; this file only feeds it stream data, list growth, and turn-settled.
  const responseRating = useResponseRating();

  const route = useRoute();

  const projectId = computed(() => project.value?.id ?? (route.params.id as string | undefined) ?? "");
  const projectName = computed(() => project.value?.projectName ?? "");

  const conversationId = ref(crypto.randomUUID());
  const errorMessage = ref<string | null>(null);

  // The chat input lives here — the v6 Chat class no longer manages input state.
  const input = ref("");

  // Synchronous pending flag: the SDK's status lags by a microtask while the fetch is set up, which
  // would otherwise delay the typing indicator. Cleared on the assistant's first token or on error.
  const isPending = ref(false);

  // Local optimistic user messages, rendered with a stable id from the moment of send. Without this
  // the bubble briefly disappears when Vue swaps :key from optimistic-id → SDK-id.
  const optimisticUserMessages = ref<OptimisticMessage[]>([]);

  function formatError(err: Error): string {
    const msg = err.message.toLowerCase();
    if (msg.includes("failed to fetch") || msg.includes("networkerror") || msg.includes("load failed")) {
      return "Could not reach the AI service. Make sure it is running and try again.";
    }
    if (msg.includes("401") || msg.includes("unauthorized")) {
      return "Authentication error. Please refresh the page and try again.";
    }
    if (msg.includes("429") || msg.includes("rate limit")) {
      return "Too many requests. Please wait a moment and try again.";
    }
    if (msg.includes("503") || msg.includes("unavailable")) {
      return "The AI service is temporarily unavailable. Please try again shortly.";
    }
    return err.message || "Something went wrong. Please try again.";
  }

  // Budget/usage denials arrive as a data-error stream part (the stream then ends without an assistant
  // message). nextBudgetAvailableAt is when the rolling token budget frees up again.
  function formatStreamError(data: { code?: string; message?: string; nextBudgetAvailableAt?: string | null }): string {
    if (data.code === "budget_exceeded") {
      if (data.nextBudgetAvailableAt) {
        const availableAt = new Date(data.nextBudgetAvailableAt);
        const sameDay = availableAt.toDateString() === new Date().toDateString();
        const time = availableAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
        return `AI budget exhausted — more available after ${sameDay ? time : `${availableAt.toLocaleDateString()} ${time}`}.`;
      }
      return "AI budget exhausted. Please try again later.";
    }
    return data.message || "Something went wrong. Please try again.";
  }

  const chat = new Chat({
    transport: new DefaultChatTransport({
      api: agentApiUrl,
      fetch: (url: RequestInfo | URL, options?: RequestInit) => {
        const token = user.value?.accessToken ?? localStorage.getItem("carbon0.accessToken") ?? "";
        return fetch(url, {
          ...options,
          headers: {
            ...(options?.headers as Record<string, string>),
            Authorization: `Bearer ${token}`,
          },
        });
      },
      // Sends the raw UIMessage[] history — only it can carry a tool call with its approval state — read
      // inside prepareSendMessagesRequest (not the static `body`) so fields reflect current values at request time.
      prepareSendMessagesRequest: ({ messages: msgs }) => ({
        body: {
          messages: msgs,
          projectId: projectId.value,
          userId: user.value?.id ?? "",
          conversationId: conversationId.value,
          stream: true,
        },
      }),
    }),
    // Once the user answers a tool-approval request, re-send the history automatically so the
    // agent resumes (runs the approved tool, or continues past a denied one).
    sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithApprovalResponses,
    onData: (part) => {
      responseRating.onStreamData(part);
      if (part.type === "data-error") {
        errorMessage.value = formatStreamError(part.data as Parameters<typeof formatStreamError>[0]);
        isPending.value = false;
      }
    },
    onError: (err) => {
      errorMessage.value = formatError(err);
      isPending.value = false;
    },
  });

  const isLoading = computed(() => chat.status === "submitted" || chat.status === "streaming");

  // Replaces SDK user messages with optimistic equivalents (matched by text content) so :key stays
  // stable across the SDK takeover. Unacked optimistic messages are appended at the end.
  const displayMessages = computed((): UIMessage[] => {
    const sdk = chat.messages;
    const optimistic = optimisticUserMessages.value;
    if (optimistic.length === 0) return sdk;

    const optByContent = new Map(optimistic.map((m) => [getMessageText(m).trim(), m]));
    const merged: UIMessage[] = sdk.map((m) => {
      if (m.role === "user") {
        const key = getMessageText(m).trim();
        const matched = optByContent.get(key);
        if (matched) {
          optByContent.delete(key);
          return matched;
        }
      }
      return m;
    });
    for (const pending of optByContent.values()) {
      merged.push(pending);
    }
    // Pre-stub window: appends a synthetic empty assistant message while waiting for the SDK's own
    // stub, so AssistantMessage owns the thinking indicator end-to-end.
    if ((isLoading.value || isPending.value) && merged.at(-1)?.role === "user") {
      merged.push({
        id: "thinking-placeholder",
        role: "assistant",
        parts: [],
      });
    }
    return merged;
  });

  // FE-only skill invocation: a pinned skill prepends one instruction line to the outgoing text at
  // send-assembly time only. retry() never passes skillName, so a re-send can't double-prepend it.
  function submit(_e: Event, opts: { skipOptimistic?: boolean; skillName?: string } = {}) {
    const rawText = input.value.trim();
    if (!rawText) return;
    const text = opts.skillName ? `Use the "${opts.skillName}" skill for this request.\n${rawText}` : rawText;

    errorMessage.value = null;
    isPending.value = true;
    // Sending a new message closes any open "How was this answer?" row: the user has moved on, and a
    // prompt lingering above the new turn would read as asking about the wrong answer.
    responseRating.controller.dismissAll();
    if (!opts.skipOptimistic) {
      optimisticUserMessages.value = [
        ...optimisticUserMessages.value,
        {
          id: `optimistic-${crypto.randomUUID()}`,
          role: "user",
          parts: [{ type: "text", text }],
        },
      ];
    }
    void chat.sendMessage({ text });
    input.value = "";
  }

  // Retries the last user message in place. Reuses the existing optimistic bubble so :key stays
  // stable; only the network request is re-fired.
  function retry() {
    const last = optimisticUserMessages.value.at(-1);
    if (!last) return;
    input.value = getMessageText(last);
    submit(new Event("submit"), { skipOptimistic: true });
  }

  function newChat() {
    // Cancel any in-flight stream so old-tenant chunks can't repopulate the cleared list.
    chat.stop();
    chat.messages = [];
    settledDataToolCallIds.clear();
    pendingModelRefresh = false;
    optimisticUserMessages.value = [];
    errorMessage.value = null;
    isPending.value = false;
    conversationId.value = crypto.randomUUID();
    responseRating.reset();
  }

  // Hands the pending flag off to the SDK once it starts streaming or settles. The synthetic
  // "thinking-placeholder" doesn't count as a real reply — that's our own pre-stub indicator.
  watch(displayMessages, (msgs) => {
    const last = msgs.at(-1);
    if (last?.role === "assistant" && last.id !== "thinking-placeholder") isPending.value = false;
  });
  watch(isLoading, (loading, wasLoading) => {
    if (!loading && wasLoading) isPending.value = false;
  });

  // Flags a dashboard refresh once any non-render assistant tool finishes. The deep watch fires per
  // chunk and replays on approval-resume, so dedupe by toolCallId; the refetch itself waits for turn-settled.
  watch(
    () => chat.messages,
    (messages) => {
      responseRating.attachPendingInteractionId(messages);
      for (const message of messages) {
        if (message.role !== "assistant") continue;
        for (const part of message.parts) {
          if (!isToolOrDynamicToolUIPart(part) || part.state !== "output-available") continue;
          const toolCallId = part.toolCallId;
          if (!toolCallId || settledDataToolCallIds.has(toolCallId)) continue;
          if (isUiRenderToolName(getToolOrDynamicToolName(part))) continue;
          settledDataToolCallIds.add(toolCallId);
          pendingModelRefresh = true;
        }
      }
    },
    { deep: true }
  );

  // Once the turn settles, re-syncs both the dashboard and the Mapping view's inventory if a data tool
  // ran — refreshDashboard alone leaves the inventory stale. Fires once on the loading edge, not per chunk.
  watch(isLoading, (loading, wasLoading) => {
    if (!loading && wasLoading && pendingModelRefresh) {
      pendingModelRefresh = false;
      void dashboard.refreshDashboard();
      void projectStore.fetchProjectMaterials();
    }
  });

  // Same settled edge, read from the SDK's own list (not displayMessages, whose synthetic
  // thinking-placeholder must never be counted as a response).
  watch(isLoading, (loading, wasLoading) => {
    if (!loading && wasLoading) responseRating.onTurnSettled(chat.messages);
  });

  function hasRealAssistantReply() {
    const last = displayMessages.value.at(-1);
    return last?.role === "assistant" && last.id !== "thinking-placeholder";
  }

  let stuckTimer: ReturnType<typeof setTimeout> | null = null;
  watch(isLoading, (loading, wasLoading) => {
    if (loading) {
      if (stuckTimer) clearTimeout(stuckTimer);
      stuckTimer = setTimeout(() => {
        if (!hasRealAssistantReply() && !errorMessage.value) {
          errorMessage.value = "The AI Agent didn't respond. Try again or refresh.";
        }
      }, STUCK_TIMEOUT_MS);
    } else if (wasLoading) {
      if (stuckTimer) {
        clearTimeout(stuckTimer);
        stuckTimer = null;
      }
      if (!hasRealAssistantReply() && !errorMessage.value) {
        errorMessage.value = "The AI Agent didn't respond. Try again or refresh.";
      }
    }
  });

  // Cross-tenant safety: wipe messages + conversationId on any real tenant change.
  // Skips the initial undefined → tenantId transition so first login doesn't bump.
  watch(
    () => user.value?.tenantId,
    (newId, oldId) => {
      if (newId !== oldId && oldId !== undefined) newChat();
    }
  );

  return {
    messages: displayMessages,
    input,
    isLoading,
    isPending,
    errorMessage,
    projectId,
    projectName,
    // Bumped by newChat() on every reset path (explicit new chat, panel close, empty project,
    // cross-tenant switch). ChatInput watches it to clear any pinned skill on conversation reset.
    conversationId,
    handleSubmit: submit,
    newChat,
    stop: () => chat.stop(),
    retry,
    // Answer a pending tool-approval request. sendAutomaticallyWhen (above) resumes the stream.
    respondToApproval: (approvalId: string, approved: boolean, reason?: string) =>
      chat.addToolApprovalResponse({ id: approvalId, approved, reason }),
    // Provided to AssistantMessage's rating prompt via ResponseRatingKey.
    responseRating: responseRating.controller,
  };
}
